import SchemaUtils, {FieldWithName} from '@/lib/schema-utils';
import React, {CSSProperties, ReactNode} from 'react';
import util from '@/lib/util';
import * as DateUtil from '@/lib/dateutils';
import store from '@/store/store';
import ActivityLabel from '../ActivityLabel/ActivityLabel';
import FinancialHelper from '@/Financial/FinancialHelper';
import IconButton from '../IconButton/IconButton';
import Icons from '../IconButton/Icons';
import Firm from '@/entities/firm';
import TextField from '../TextField';
import AddressHelper from '@/Contacts/Address/AddressHelper';
import ResourceHelper from '@/Contacts/Resource/ResourceHelper';
import LookupRegistry from '../LookupSelect/LookupRegistry';
import CaseHelper from '@/Case/CaseHelper';
import * as CampaignHelper from '@/CRM/Marketing/Campaigns/CampaignHelper';
import {
    formatFML,
    formatFMLFromId,
    formatLFMFromId,
    formatUserName,
    formatUserNameFromId,
    getUserInitials
} from "@/Contacts/ContactFormat";
import find from "@/store/find";
import * as duration from "@/lib/picos/duration";
import ActivityCategory from "@/entities/activityCategory";
import ActivityType from "@/entities/activityType";
import Contact from "@/entities/contact";
import Discount from "@/entities/discount";
import Expense from "@/entities/expense";
import FlatFee from "@/entities/flatFee";
import FlatFeeMilestone from "@/entities/flatFeeMilestone";
import RedFlagType from "@/entities/redFlagType";
import Lead from "@/entities/lead";
import Task from "@/entities/task";
import Transaction from "@/entities/transaction";
import Category from "@/entities/category";
import Event from "@/entities/event";
import Identification from "@/entities/identification";
import Matter from "@/entities/matter";
import Milestone from "@/entities/milestone";
import Referral from "@/entities/referral";
import Resource from "@/entities/resource";
import Invoice from "@/entities/invoice";
import {EntityMetaCompareResult, EntityMetaFilter, EntityMetaNameMap, EntityMetaOptions, EntityMetaTransform} from "./EntityMeta";
import type {EntitySchema} from "@/@types/schema";

const ENTITY = new Firm({});

class EntityMetaHelper {
    /**
     * Format a duration as hh:mm:ss
     * @param {number} value - duration in milliseconds
     * @returns {string}
     */
    static formatDuration(value: number): string {
        if (!value) return '';
        return duration.formatDuration(value, 'hms');
    }

    /**
     * Format an icon/label for the ActivityType.
     *
     * @param {ActivityType} activityType
     * @returns {JSX.Element|string}
     */
    static formatActivityType(activityType: ActivityType): ReactNode {
        // Need the category name for this activityType
        const activityCategory = find<ActivityCategory>('activityCategories', activityType.activityCategoryId);
        if (activityType.status === 'D') return <ActivityLabel category={activityCategory} type={activityType}/>;
        else return <ActivityLabel category={activityCategory} type={activityType} deleted={true}/>

        // if (activityType.status === 'D')
        //     return <>{activityCategory.name}/{activityType.name} <span style={{color: 'maroon'}}>(deleted)</span></>;
        // else return `${activityCategory.name}/${activityType.name}`;
    }

    /**
     * See if the item exists in the array.
     *
     * @param {array} arr
     * @param {any} item
     * @returns {boolean}
     */
    static includes(arr: any[], item: any): boolean {
        if (!Array.isArray(arr)) return false;
        else return arr.some(o => util.deepCompare(o, item));
    }

    /**
     * Do a deep compare between all elements of each array, ensuring that all elements of arr1 are in arr2.
     *
     * @param {array} arr1
     * @param {array} arr2
     */
    static includesAll(arr1: any[], arr2: any[]): boolean {
        return arr1.every(item => {
            return EntityMetaHelper.includes(arr2, item);
        });
    }

    static renderClients(matter: Matter) {
        return matter.clients
            .map(id => find<Contact>('contacts', id))
            .map(contact => {
                const c = formatFML(contact, true);

                if (!c) console.warn('No client found for matter', matter.id, matter.clients);

                return c;
            })
            .filter(util.existsFilter)
            .join(' & ');
    }
    /**
     *
     * @param {any} v
     * @param {FieldWithName} field
     * @param {EntityMetaTransform} transform
     * @param {any} before
     * @param {any} after
     */
    static handleFormat(v: any, field: any, transform: Function, before: any, after: any): ReactNode {
        const entityType = field.ref || field.entityType;
        const isRef = !!field.ref;

        if(util.deepCompare(before, after)) return null;

        // Handle nulls
        if (v === null || v === undefined) return transform('');

        // Handle dates
        else if (field.type === 'date') return transform(DateUtil.formatDate(v, false, false, false));

        // Handle timestamps
        else if (field.type === 'timestamp') return transform(DateUtil.formatDateTime(v));

        // Handle booleans
        else if (field.type === 'boolean') return transform(v === true ? 'Yes' : 'No');

        //  Handle JSON(B)
        else if (field.type.startsWith('json')) return transform(v, before, after);

        // Handle reference types
        else if (entityType) {
            // Need to convert this uuid to an object
            switch (entityType) {
                case 'activityType':
                    const activityType = find<ActivityType>('activityTypes', v);
                    if (activityType) return EntityMetaHelper.formatActivityType(activityType);
                    else return transform('Activity Not Found');
                case 'caseTypeId':
                case 'caseType':
                    return transform(CaseHelper.formatCaseTypeFromId(v));
                case 'address':
                    return AddressHelper.renderAddress(v);
                case 'client':
                    const client = find<Contact>('contacts', v);
                    if (client) return transform(formatFML(client));
                    else return transform('[Client Deleted]');
                case 'contact':
                    const contact = isRef ? v : find<Contact>('contacts', v);
                    if (contact) return transform(formatFML(contact));
                    else return transform('Contact Not Found');
                case 'campaign':
                    return transform(CampaignHelper.getCampaignFromId(v)?.name || 'No Campaign Found');
                case 'category':
                    const category = isRef ? v : find<Category>('categories', v);
                    if (category) {
                        if (category.status === 'D') return <>{transform(category.name)} <span
                            style={{color: 'maroon'}}>(deleted)</span></>;
                        else return transform(category.name);
                    }
                    else return transform('Category Not Found');
                case 'discount':
                    const discount = find<Discount>('discounts', v);
                    if (discount) {
                        const desc = discount.description || (discount.method === 'P' ? `${discount.percentage}%` : util.formatCurrency(discount.simple));
                        return transform(desc);
                    }
                    else return transform('[Discount Deleted]');
                case 'comment':
                    // Always a ref
                    const user = getUserInitials(v.addedBy);
                    return transform(<div className='entity-meta__flexed'>
                        <span><b>{user}</b></span>
                        {TextField.renderHTML(v.note)}
                    </div>);
                case 'event':
                    const event: Event = v;
                    const lookup = LookupRegistry.getLookupById(event.category)?.key ?? '???';
                    const occurredOn = DateUtil.formatDate(event.occurredOn);
                    const participants = event.participants
                                              .map(pid => store.getState()
                                                               .participants
                                                               .find(p => p.id === pid)
                                              )
                                              .filter(util.existsFilter)
                                              .map(p => formatFMLFromId(p.contactId))
                                              .join(', ');

                    return <div className='entity-meta__flexed'>
                        <b>{lookup}</b>
                        <span>{` on ${occurredOn} ${participants ? 'with ' + participants : ''}`.trim()}</span>
                        <i dangerouslySetInnerHTML={TextField.createMarkup(event.description)}/>
                    </div>
                case 'expense':
                    const expense = find<Expense>('expenses', v);
                    if (expense) {
                        const amount = util.formatCurrency(expense.cost * expense.quantity);
                        const description = expense.description || 'Expense';
                        return transform(`${description}: ${amount}`);
                    }
                    else return transform('[Expense Deleted]');
                case 'flatFee':
                    const flatFee = isRef ? v : find<FlatFee>('flatFees', v);

                    // Tasks has a flatFeeBucket with entity type flatFee
                    if (flatFee && !isRef) {
                        const contact = flatFee.relatedTo ? formatFMLFromId(flatFee.relatedTo, true) : '';
                        return transform(contact ? `${flatFee.name} (${contact})` : flatFee.name);
                    }
                    else if (flatFee && isRef) {
                        // This is the normal ref type
                        const name = v.name;
                        const amount = util.formatCurrency(v.amount, true);
                        const description = v.description;
                        const earned = v.earned ? '[earned]' : '';
                        const isDefault = v.isDefault ? '[is default]' : '';
                        const completionDate = v.completionDate ? 'Earned on ' + DateUtil.formatDate(v.completionDate) : '';
                        return transform(`${name}: ${amount} ${earned} ${isDefault} ${completionDate} -- ${description}`);
                    }
                    else return transform('Flat Fee Not Found');
                case 'flatFeeMilestone':
                    const ffm = find<FlatFeeMilestone>('flatFeeMilestones', v);
                    if (ffm) return transform(`${ffm.name}: ${util.formatCurrency(ffm.amount)}`);
                    else return transform('[Milestone Deleted]');
                case 'identification':
                    const id: Identification = v;
                    return <div style={{flexDirection: 'column'}} className='entity-meta__flexed'>
                        <b>{id.method} {id.identifier}</b>
                        {id.issuer ? <span style={{marginLeft: '1em'}}>Issued by {id.issuer}</span> : null}
                        {id.issuedOn ? <span style={{marginLeft: '1em'}}>Issued on {id.issuedOn}</span> : null}
                        {id.expiresOn ? <span style={{marginLeft: '1em'}}>Expires on {id.expiresOn}</span> : null}
                        {id.additionalInfo ? <div style={{marginLeft: '1em'}}>{TextField.renderHTML(id.additionalInfo)}</div> : null}
                    </div>;
                case 'invoice':
                    const invoice: Invoice = isRef ? v : FinancialHelper.getInvoiceById(v);
                    if (invoice) {
                        return transform(`Invoice #${invoice.invoiceNumber}`);
                    }
                    else return transform(`[Invoice ${v} Deleted]`);
                case 'lead':
                    const lead = find<Lead>('leads', v);
                    if (lead) return transform(lead.name);
                    else return transform('[Lead Deleted]');
                case 'matter':
                    const matter: Matter = isRef ? v : CaseHelper.getMatterFromId(v);
                    if (matter) return transform(matter.name);
                    else return transform(`[Case ${v} Deleted]`);
                case 'milestone':
                    const ms: Milestone = v;

                    before = before.filter((m: Milestone) => m.dateCompleted).map((m: Milestone) => {
                        const {name, dateCompleted, followupDate, notes, reasons} = m;
                        return {name, dateCompleted, followupDate, notes, reasons};
                    });

                    after = after.filter((m: Milestone) => m.dateCompleted).map((m: Milestone) => {
                        const {name, dateCompleted, followupDate, notes, reasons} = m;
                        return {name, dateCompleted, followupDate, notes, reasons};
                    });

                    if(util.deepCompare(before, after)) return transform(null);

                    if(!ms.dateCompleted) return transform(null);
                    else {
                        const reasons = ms.reasons?.map(rid => LookupRegistry.getLookupById(rid)?.key).join('; ');

                        return transform(<div style={{flexDirection: 'column'}} className='entity-meta__flexed'>
                            <b>{ms.name}</b>
                            {ms.dateCompleted ? <span style={{marginLeft: '1em'}}>Completed on {DateUtil.formatDateTime(ms.dateCompleted)}</span> : null}
                            {ms.followupDate ? <span style={{marginLeft: '1em'}}>Follow up on {DateUtil.formatDateTime(ms.followupDate)}</span> : null}
                            {ms.notes ? <span style={{marginLeft: '1em'}}>Notes: {ms.notes}</span> : null}
                            {ms.reasons.length > 0 ? <span style={{marginLeft: '1em'}}>Reasons: {reasons}</span> : null}
                        </div>);
                    }
                case 'participant':
                    return transform(formatLFMFromId(v.contactId));
                case 'redFlag': {
                    const redFlagType = find<RedFlagType>('redFlagTypes', v.redFlagTypeId)?.name;
                    const boosted = v.boost ? <IconButton type={Icons.LONG_ARROW_UP} color='red'/> : null;
                    const checked = <IconButton type={v.checked ? Icons.CHECKED : Icons.UNCHECKED}
                                                label={redFlagType}
                    />;
                    const notes = v.notes;

                    return transform(<div className='entity-meta__flexed'>
                        {boosted}{checked}
                        <span style={{fontStyle: 'italic', marginLeft: '1.5em'}}>{notes}</span>
                    </div>);
                }
                case 'referral':
                    const r: Referral = v;
                    const referrer = formatFMLFromId(r.referrer);
                    const referral = find<Lead>('leads', r.referral)?.name || 'No Lead Found';
                    const refdate = DateUtil.formatDate(r.referredOn);
                    return transform(`${referrer} referred ${referral} on ${refdate}`);
                case 'resource':
                    const resource: Resource = v;
                    if (resource.type === 'email') return ResourceHelper.renderEmail(resource);
                    else if (resource.type === 'phone') return ResourceHelper.renderPhone(resource);
                    else return resource.value;
                case 'question':
                    return transform(v);
                case 'answer':
                    return transform(v);
                case 'task':
                    const task = find<Task>('tasks', v);
                    if (task) return transform(task.title || 'Untitled Task');
                    else return transform('[Task Deleted]');
                case 'transaction':
                    const tx = find<Transaction>('transactions', v);
                    if (tx) return transform(`${util.formatCurrency(tx.amount)} (${DateUtil.formatDate(tx.txDate)})`);
                    else return transform('[Transaction Deleted]');
                case 'user':
                    if (isRef) return transform(formatUserName(v));
                    else return transform(formatUserNameFromId(v));
                default:
                    if (['string','number','uuid'].includes(typeof (v))) return transform(v);
                    else return transform(JSON.stringify(v));
            }
        }

        // Handle objects not otherwise handled
        else if (typeof (v) === 'object') return transform(JSON.stringify(v));

        // Handle everything else
        else return transform(v);
    }

    /**
     *
     * @param {FieldWithName} field
     * @param {any} value
     * @param {string} key
     * @param {any} before - the whole previous value
     * @param {any} after - the whole current value
     * @param {EntityMetaOptions} options
     * @returns {ReactNode}
     */
    static formatField(field: FieldWithName, value: any, key: string, before: any, after: any, options: EntityMetaOptions): ReactNode {
        const isBefore: boolean = ((value ?? null) === (before ?? null));
        const isAfter: boolean = ((value ?? null) === (after ?? null));
        const isArray: boolean = field.type.endsWith('[]');
        const isJson: boolean = field.type.startsWith('json');
        const isRef: boolean = !!field.ref;
        const defaultTransform: EntityMetaTransform = t => t;
        const transform = options.transforms?.[field.name] ?? defaultTransform;
        const defaultFilter: EntityMetaFilter = (_: any) => true;
        const filter = options.filters?.[field.name] ?? defaultFilter;

        let j = 0;

        // If array of JSON values
        if (isArray && isJson) return value?.filter(filter).map((v: any) => {
            // If we are looking at the previous value, and this value doesn't exist in the current value, color it red.
            // If we are looking at the current value, and this value doesn't exist in the previous value, color it green.`
            let style: CSSProperties = {};
            if (before?.length > 1 && after?.length > 1) {
                if (isBefore && !EntityMetaHelper.includes(after, v)) {
                    style.color = 'maroon';
                    style.textDecoration = 'line-through';
                }
                if (isAfter && !EntityMetaHelper.includes(before, v)) style.color = 'green';
            }

            return <div key={`${key}-${j++}`}
                        style={style}>{EntityMetaHelper.handleFormat(v, field, transform, before, after)}</div>;
        });

        // If array of ref fields
        if (isArray && isRef) return value?.filter(filter).map((v: any) => {
            // If we are looking at the previous value, and this value doesn't exist in the current value, color it red.
            // If we are looking at the current value, and this value doesn't exist in the previous value, color it green.
            let style: CSSProperties = {};
            if (before?.length > 1 && after?.length > 1) {
                if (isBefore && !EntityMetaHelper.includes(after, v)) {
                    style.color = 'maroon';
                    style.textDecoration = 'line-through';
                }
                if (isAfter && !EntityMetaHelper.includes(before, v)) style.color = 'green';
            }

            return <div key={`${key}-${j++}`}
                        style={style}>{EntityMetaHelper.handleFormat(v, field, transform, before, after)}</div>;
        });

        // If array, map to values
        else if (isArray) {
            const prevArr = before || [];
            const currArr = after || [];

            // When both sides have items, show only the delta (added/removed)
            if (prevArr.length > 0 && currArr.length > 0) {
                let changed: any[];
                let style: CSSProperties;
                let label: string;

                if (isBefore) {
                    changed = (value || []).filter(filter).filter((v: any) => !currArr.includes(v));
                    style = {color: 'maroon', textDecoration: 'line-through'};
                    label = `Removed ${changed.length}`;
                }
                else {
                    changed = (value || []).filter(filter).filter((v: any) => !prevArr.includes(v));
                    style = {color: 'green'};
                    label = `Added ${changed.length}`;
                }

                if (changed.length === 0) return <div key={`${key}-${j++}`}/>;

                return <>
                    <div key={`${key}-${j++}`} style={{fontStyle: 'italic', ...style}}>{label}:</div>
                    {changed.map((v: any) => (
                        <div key={`${key}-${j++}`}
                             style={style}>{EntityMetaHelper.handleFormat(v, field, transform, before, after)}</div>
                    ))}
                </>;
            }

            // One side is empty — show all items (entire array was added or removed)
            return value?.filter(filter).map((v: any) => {
                let style: CSSProperties = {};
                if (isBefore) {
                    style.color = 'maroon';
                    style.textDecoration = 'line-through';
                }
                if (isAfter) style.color = 'green';
                return <div key={`${key}-${j++}`}
                            style={style}>{EntityMetaHelper.handleFormat(v, field, transform, before, after)}</div>;
            }) || <div key={`${key}-${j++}`}/>;
        }

        // All others
        else return <div
                key={`${key}-${j++}`}>{EntityMetaHelper.handleFormat(value, field, transform, before, after)}</div>;
    }

    /**
     * Format the name of the field. Check in the nameMap for a custom label.
     *
     * @param {string} name
     * @param {EntityMetaNameMap} nameMap
     * @returns {string}
     */
    static formatName(name: string, nameMap: EntityMetaNameMap): string {
        const customName = nameMap[name];
        if (customName) return customName;
        else return util.camelCaseToWords(name);
    }

    /**
     * Get the default value for this field.
     *
     * @param {EntitySchema} schema
     * @param {string} name
     */
    static getDefault(schema: EntitySchema, name: string) {
        const field = schema.fields[name];
        let def: any = field['default'];
        if (def === null || def === undefined) {
            // If this is a boolean, return false
            if (field.type === 'boolean') def = false;
            else if (field.type === 'float') def = 0.0;
            else if (field.type === 'int' || field.type === 'bigint') def = 0;
            else if (field.type.endsWith('[]')) def = [];
            else if (field.type.startsWith('json')) def = {};
        }

        return def || null;
    }

    static hasRefFields(entityClass: any, ignored: string[]): boolean {
        // Start by stripping any transient fields or base fields
        const schema = entityClass.getSchema();
        return SchemaUtils.getFields(schema).filter(f => !!f.ref && !ignored.includes(f.name)).length > 0;
    }

    static stripIrrelevant(arr: []) {
        return arr.map(o => {
            const keys = Object.keys(o);
            keys.forEach(key => {
                if (ENTITY.isTransientProperty(key) ||
                ['importId', 'importSource', 'firmId'].includes(key)) {
                    delete o[key];
                }
            });

            return o;
        });
    }

    /**
     * Return a set of key/values for things that changed.  Return both the old and new values.
     *
     * @param {any} entityClass
     * @param {<<T>>} prev
     * @param {<<T>>} curr
     * @param {EntityMetaOptions} options
     * @return {Array.<{name: string, prev: <<T>>, curr: <<T>>}>}
     */
    static compareVersions<T>(entityClass: any, prev: T, curr: T, options: any): EntityMetaCompareResult[] {
        const {nameMap, ignoreFields} = options;

        // Used for unique keys
        let i = 0;

        // Start by stripping any transient fields or base fields
        const schema = entityClass.getSchema();
        const fields = SchemaUtils.getFields(schema);
        const schemaName = schema.entity;

        function processReturn(name: string, field: FieldWithName, prevField: any, currField: any) {
            return {
                name: EntityMetaHelper.formatName(name, nameMap),
                prev: EntityMetaHelper.formatField(field, prevField, `P-${schemaName}-${name}-${i++}`, prevField, currField, options),
                curr: EntityMetaHelper.formatField(field, currField, `C-${schemaName}-${name}-${i++}`, prevField, currField, options)
            }
        }

        return fields.map(field => {
            const name = field.name;
            const isRef = !!field.ref;

            // If we are ignoring this field, just return null
            if (ignoreFields.includes(name)) return null;

            // Get the field values for the previous and current versions
            // const def = EntityMetaHelper.getDefault(schema, name);
            let prevField = prev ? prev[name] : null;
            let currField = curr ? curr[name] : null;

            // Handle numeric types
            if (['float', 'int', 'integer', 'bigint'].includes(field.type)) {
                // 0 equals null for comparison's sake
                if ((prevField || 0) === (currField || 0)) return null;
            }

            // Handle boolean types
            else if (field.type === 'boolean') {
                // null equals false for comparison's sake
                if ((prevField ?? false) === (currField ?? false)) return null;
            }

            // Handle date/time types
            else if (['date', 'datetime', 'timestamp'].includes(field.type)) {
                // Make sure we don't have strings
                if (typeof (prevField) === 'string') prevField = new Date(prevField);
                if (typeof (currField) === 'string') currField = new Date(currField);
            }

            // Handle json(b) types (quick check only)
            else if (field.type.startsWith('json')) {
                if (field.type.endsWith('[]')) {
                    // Empty arrays are equal
                    if (prevField?.length === 0 && currField?.length === 0) return null;
                }
                else {
                    // Make sure string entries of '{}' become {}
                    if (prevField === '{}') prevField = {};
                    if (currField === '{}') currField = {};
                    if (Object.entries(prevField ?? {}).length === 0 && Object.entries(currField ?? {}).length === 0) return null;
                }
            }

            // Handle entity/entityType (not ref) fields
            if ((field.entityType || field.entity) && field.type.endsWith('[]')) {
                // Compare the arrays
                prevField = prevField || [];
                currField = currField || [];

                const same = (prevField.length === currField.length) &&
                             (util.arrayIntersection(
                                     prevField.map((v: any) => JSON.stringify(v)),
                                     currField.map((v: any) => JSON.stringify(v))
                                 ).length === prevField.length
                             );
                if (!same) {
                    return processReturn.call(this, name, field, prevField, currField);
                }
                else return null;
            }

            // Handle ref (array) fields
            else if (isRef && field.type.endsWith('[]')) {
                // Compare the arrays
                prevField = EntityMetaHelper.stripIrrelevant((prevField || []).map((o: any) => util.stripObject(o)));
                currField = EntityMetaHelper.stripIrrelevant((currField || []).map((o: any) => util.stripObject(o)));

                const same = (prevField.length === currField.length) && // quick check for length without deep compare
                             EntityMetaHelper.includesAll(prevField, currField); // deep check

                if (!same) {
                    return processReturn.call(this, name, field, prevField, currField);
                }
                else return null;
            }

            // Handle other types of arrays and objects
            else if (field.entityType ||
                     (prevField !== null && typeof (prevField) === 'object') ||
                     (currField !== null && typeof (currField) === 'object')) {
                if (!util.deepCompare(prevField, currField)) return processReturn.call(this, name, field, prevField, currField);
                else return null;
            }

            else if (prevField !== currField) return processReturn.call(this, name, field, prevField, currField);

            else return null;
        }).filter(o => !!o);
    }
}

export default EntityMetaHelper;
